import * as React from "react";
import { ResponsiveSheet } from "./ResponsiveSheet";
import { Phone, PencilSimple, Crown, CheckCircle, ProhibitInset, XCircle, Play } from "@phosphor-icons/react";
import type { Appointment, DailyTimelineLabels, TransitionStatus } from "./types";
import { fmtTime } from "./utils";
import { cn } from "@/lib/utils";

interface AppointmentDetailSheetProps {
  appointment: Appointment | null;
  open: boolean;
  labels: DailyTimelineLabels;
  onClose: () => void;
  onEdit?: (a: Appointment) => void;
  onCallCustomer?: (phone: string) => void;
  onAction?: (a: Appointment, next: TransitionStatus) => void;
}

export function AppointmentDetailSheet({
  appointment, open, labels, onClose, onEdit, onCallCustomer, onAction,
}: AppointmentDetailSheetProps) {
  if (!appointment) {
    return (
      <ResponsiveSheet open={open} onClose={onClose} title={labels.detailsTitle} closeLabel={labels.closeSheet}>
        <></>
      </ResponsiveSheet>
    );
  }
  const a = appointment;
  const isPast = a.status === "completed" || a.status === "cancelled" || a.status === "no_show";

  const ActionButton = ({
    onClick, tone, icon: Icon, children,
  }: {
    onClick: () => void;
    tone: "primary" | "success" | "warning" | "danger" | "info" | "neutral";
    icon: typeof Phone;
    children: React.ReactNode;
  }) => {
    const toneClass = {
      primary: "bg-biz-primary text-biz-primary-foreground hover:bg-biz-primary-hover",
      success: "bg-biz-success text-biz-success-foreground hover:opacity-90",
      warning: "bg-biz-warning text-biz-warning-foreground hover:opacity-90",
      danger: "bg-biz-danger text-biz-danger-foreground hover:opacity-90",
      info: "bg-biz-info text-biz-info-foreground hover:opacity-90",
      neutral: "border border-biz-border text-biz-foreground hover:bg-biz-muted",
    }[tone];
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "h-14 px-4 rounded-biz-btn font-semibold text-sm flex items-center justify-center gap-2 biz-focus-ring biz-motion-safe transition-colors",
          toneClass,
        )}
      >
        <Icon size={18} weight="regular" />
        {children}
      </button>
    );
  };

  return (
    <ResponsiveSheet open={open} onClose={onClose} title={labels.detailsTitle} closeLabel={labels.closeSheet}>
      <div className="space-y-4">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs uppercase tracking-wider text-biz-foreground-muted font-semibold">
              {labels.statusLabels[a.status]}
            </span>
            {a.isVip && (
              <span
                className="inline-flex items-center gap-0.5 rounded-full bg-biz-accent-subtle px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: "hsl(var(--biz-accent))" }}
              >
                <Crown size={12} weight="regular" /> {labels.vipBadge}
              </span>
            )}
          </div>
          <h3 className="font-display text-xl font-semibold">{a.petName}</h3>
          <p className="text-sm text-biz-foreground-muted">{a.serviceName}</p>
          <p className="font-mono-num text-sm text-biz-foreground mt-1">
            {fmtTime(a.startTime)} – {fmtTime(a.endTime)}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-biz-foreground-muted">{labels.customerLabel}</div>
            <div className="text-biz-foreground">{a.customerName}</div>
          </div>
          {typeof a.price === "number" && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-biz-foreground-muted">{labels.priceLabel}</div>
              <div className="font-mono-num text-biz-foreground">
                {a.price.toLocaleString()} {a.currency ?? ""}
              </div>
            </div>
          )}
        </div>

        {a.notes && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-biz-foreground-muted mb-1">{labels.notesLabel}</div>
            <p className="text-sm text-biz-foreground bg-biz-muted/60 rounded-md p-3">{a.notes}</p>
          </div>
        )}

        {/* Actions */}
        {!isPast && (
          <div className="grid grid-cols-2 gap-2 pt-2">
            {a.status === "in_progress" ? (
              <>
                <ActionButton onClick={() => onAction?.(a, "completed")} tone="success" icon={CheckCircle}>
                  {labels.completeAction}
                </ActionButton>
                <ActionButton onClick={() => onAction?.(a, "cancelled")} tone="danger" icon={XCircle}>
                  {labels.cancelAction}
                </ActionButton>
              </>
            ) : (
              <>
                <ActionButton onClick={() => onAction?.(a, "in_progress")} tone="info" icon={Play}>
                  {labels.markInProgressAction}
                </ActionButton>
                <ActionButton onClick={() => onAction?.(a, "no_show")} tone="warning" icon={ProhibitInset}>
                  {labels.markNoShowAction}
                </ActionButton>
              </>
            )}
            <ActionButton onClick={() => onCallCustomer?.("")} tone="neutral" icon={Phone}>
              {labels.callAction}
            </ActionButton>
            <ActionButton onClick={() => onEdit?.(a)} tone="neutral" icon={PencilSimple}>
              {labels.editAction}
            </ActionButton>
          </div>
        )}
      </div>
    </ResponsiveSheet>
  );
}
